import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Quotes, Camera, Plus, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import VoiceInput from "@/components/VoiceInput";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fileUrl(path) {
  if (!path) return null;
  return `${BACKEND}/api/files/${path}`;
}

export default function TestimonialsWall() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("share") === "story");

  useEffect(() => {
    api
      .get("/testimonials", { params: { limit: 12 } })
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="stories" data-testid="testimonials-wall" className="mx-auto max-w-7xl px-6 py-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">The angler squad</div>
          <h2 className="font-display text-4xl md:text-5xl mt-2">On the water,<br/>in our gear.</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="submit-testimonial-btn" className="btn-primary rounded-sm h-11 px-5 font-display tracking-wide self-start md:self-end">
              <Plus size={16} className="mr-2" /> Share your catch
            </Button>
          </DialogTrigger>
          <TestimonialForm onDone={() => { setOpen(false); }} />
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-64 bg-muted animate-pulse rounded-sm" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div data-testid="testimonials-empty" className="border border-dashed border-border rounded-sm p-12 text-center">
          <Quotes size={32} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-xl">No stories yet</div>
          <p className="text-sm text-muted-foreground mt-2">Be the first to show off your Jimenez88 shirt on the water.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t) => (
            <article
              key={t.id}
              data-testid={`testimonial-${t.id}`}
              className="border border-border bg-white rounded-sm overflow-hidden flex flex-col"
            >
              {t.photo_path && (
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  <img src={fileUrl(t.photo_path)} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-5 flex-1 flex flex-col">
                <Quotes size={22} weight="duotone" className="text-[#C84A31]" />
                <p className="mt-3 text-sm text-foreground/85 leading-relaxed flex-1">{t.text}</p>
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="font-display text-sm">{t.name}</div>
                  {t.location && <div className="text-xs uppercase tracking-widest text-muted-foreground mt-0.5">{t.location}</div>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function TestimonialForm({ onDone }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [text, setText] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openedAt] = useState(() => new Date().toISOString());

  function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image is too large (max 5 MB).");
    setFile(f);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  }

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !text.trim()) return toast.error("Name and story are required.");
    setSubmitting(true);
    let photo_path = null;
    try {
      if (file) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_path = up.data.path;
        setUploading(false);
      }
      await api.post("/testimonials", {
        name: name.trim(),
        location: location.trim() || null,
        text: text.trim(),
        photo_path,
        website,  // honeypot — must remain empty for humans
        form_opened_at: openedAt,
      });
      toast.success("Thanks! Your story is in the queue for review.");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
      setUploading(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent data-testid="testimonial-dialog" className="rounded-sm max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Share your catch</DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          Tell us how you caught the big one. Add a photo if you&apos;ve got one.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        {/* Honeypot — must remain empty. Hidden from users with CSS, aria-hidden, and off-screen position. */}
        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}
        >
          <label htmlFor="website">Website (leave empty)</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Your name</Label>
          <Input
            data-testid="testimonial-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Captain Mike"
            className="rounded-sm"
            maxLength={60}
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Location (optional)</Label>
          <Input
            data-testid="testimonial-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Lake Michigan, MI"
            className="rounded-sm"
            maxLength={80}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Your story</Label>
          <Textarea
            data-testid="testimonial-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Caught my biggest bass wearing the 'Reel Nice' tee..."
            rows={4}
            className="rounded-sm resize-none"
            maxLength={600}
            required
          />
          <div className="flex items-center justify-between gap-3">
            <VoiceInput
              testIdPrefix="testimonial-voice"
              label="Dictate your story"
              onTranscript={(t) =>
                setText((prev) => (prev ? `${prev.trim()} ${t}`.slice(0, 600) : t.slice(0, 600)))
              }
            />
            <div className="text-xs text-muted-foreground">{text.length}/600</div>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest">Photo (optional, max 5 MB)</Label>
          <label className="block border border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:border-[#C84A31] transition-colors">
            {preview ? (
              <img src={preview} alt="" className="max-h-40 mx-auto rounded-sm" />
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Camera size={28} weight="duotone" />
                <span className="text-sm">Click to upload a photo</span>
              </div>
            )}
            <input
              data-testid="testimonial-photo"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onFile}
              className="hidden"
            />
          </label>
        </div>
        <Button
          type="submit"
          data-testid="testimonial-submit"
          disabled={submitting}
          className="w-full btn-primary rounded-sm h-11 font-display tracking-wide"
        >
          {submitting && <Spinner size={16} className="mr-2 animate-spin" />}
          {uploading ? "Uploading photo…" : submitting ? "Submitting…" : "Submit story"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Stories are reviewed before appearing on the site.
        </p>
      </form>
    </DialogContent>
  );
}
